import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Tooltip from '@material-ui/core/Tooltip';
import WarningRoundedIcon from '@material-ui/icons/WarningRounded';
import { useSelector } from 'react-redux';
import { Container } from '@chakra-ui/react';
import { Button, Text } from 'components/v4';
import AGGridMain from 'components/tables/AGGrid/AGGridMain';
import CopyCellRenderer from 'components/tables/AGGrid/CustomCells/CopyCellRenderer';
import { LoadingIndicator } from '@/components/v4';
import useColumnState from '@/hooks/useColumnState';
import walkthroughIds from '../walkthroughIds';
import { WHOLESALER_MODES } from './v5/constants';
import useGetAllWholesalers from './v5/queries/useGetAllWholesalers';
import WholesalerEdit from './v5/WholesalerEdit';
import WholesalerActionCellRenderer from './cellRenderer/WholesalerActionCellRenderer';

const Wholesalers = () => {
    const { currentScope } = useSelector((state) => state.login);
    const { currentWholesaler, currentPartner, currentCompany } = useSelector(
        (state) => state.navigationLists,
    );
    const gridRef = useRef();

    const [mode, setMode] = useState(WHOLESALER_MODES.MAIN_TABLE);
    const [selectedWholesalerId, setSelectedWholesalerId] = useState(null);
    const [tableData, setTableData] = useState({
        columns: [],
        data: [],
    });

    const { wholesalers: wholesalerWalkthroughIds } = walkthroughIds;
    const { isLoading, isRefetching, isFetching, refetch } =
        useGetAllWholesalers(setTableData);

    const switchMode = useCallback((newMode, wholesalerId = null) => {
        setMode(newMode);
        if (wholesalerId !== null) {
            setSelectedWholesalerId(wholesalerId);
        }
    }, []);

    // Create/Update/Delete mutations already invalidate the ['wholesalers']
    // query in their own onSuccess, which refetches once the table remounts.
    // Calling refetch() here too caused a duplicate GET /wholesalers/ on every
    // save (visible as repeated network calls right after editing a wholesaler).
    const resetToMainTable = useCallback(() => {
        setMode(WHOLESALER_MODES.MAIN_TABLE);
        setSelectedWholesalerId(null);
    }, []);

    const handleAdd = useCallback(() => {
        switchMode(WHOLESALER_MODES.ADD);
    }, [switchMode]);

    const handleEdit = useCallback(
        (params) => {
            switchMode(WHOLESALER_MODES.EDIT, params.data.id);
        },
        [switchMode],
    );

    const DefaultPlanCellRenderer = useCallback((params) => {
        const planName = (() => {
            if (!params.value) return '';
            if (typeof params.value === 'string') return params.value;
            if (typeof params.value === 'object' && params.value.name) {
                return params.value.name;
            }
            return '';
        })();

        const leftAdornment = !params.value ? (
            <Tooltip title="No Default Plan has been set. This wholesaler will not be billed.">
                <span>
                    <WarningRoundedIcon style={{ color: '#ff9800' }} />
                </span>
            </Tooltip>
        ) : null;

        return (
            <CopyCellRenderer value={planName} leftAdornment={leftAdornment} />
        );
    }, []);

    const baseColDefs = useMemo(
        () => [
            {
                field: 'name',
                headerName: 'Wholesaler Name',
                flex: 1,
                minWidth: 200,
                sortable: true,
                sort: 'asc',
            },
            {
                field: 'defaultWholesalerPlan',
                headerName: 'Default Plan',
                width: 200,
                cellRenderer: DefaultPlanCellRenderer,
                valueGetter: (params) =>
                    params.data?.defaultWholesalerPlan?.name ||
                    params.data?.defaultWholesalerPlan ||
                    params.data?.defaultPlan?.name ||
                    params.data?.defaultPlan,
            },
            {
                field: 'serviceCountries',
                headerName: 'Service Countries',
                hide: true,
            },
            {
                field: 'attribute1',
                headerName: 'Wholesaler Attribute 1',
                hide: true,
            },
            {
                field: 'attribute2',
                headerName: 'Wholesaler Attribute 2',
                hide: true,
            },
            {
                field: 'attribute3',
                headerName: 'Wholesaler Attribute 3',
                hide: true,
            },
            {
                field: 'id',
                headerName: 'Wholesaler ID',
                hide: true,
            },
            {
                lockPosition: 'right',
                headerName: 'Action',
                maxWidth: 100,
                filter: false,
                suppressMovable: true,
                lockVisible: true,
                suppressColumnsToolPanel: true,
                suppressHeaderMenuButton: true,
                suppressHeaderFilterButton: true,
                enableRowGroup: false,
                resizable: false,
                sortable: false,
                cellStyle: () => ({
                    display: 'flex',
                    alignItems: 'center',
                }),
                cellRenderer: (params) => (
                    <WholesalerActionCellRenderer
                        params={params}
                        onEdit={handleEdit}
                        walkthroughIds={wholesalerWalkthroughIds?.general}
                    />
                ),
            },
        ],
        [
            DefaultPlanCellRenderer,
            handleEdit,
            wholesalerWalkthroughIds?.general,
        ],
    );

    const { columns: colDefs, onColumnVisible } = useColumnState(baseColDefs);

    const tableDataWithColumns = useMemo(
        () => ({
            columns: colDefs,
            data: tableData.data,
        }),
        [colDefs, tableData.data],
    );

    useEffect(() => {
        if (
            mode === WHOLESALER_MODES.MAIN_TABLE &&
            tableData.data.length === 0 &&
            !isLoading
        ) {
            refetch();
        }
    }, [mode, tableData.data.length, isLoading, refetch]);

    const prevScopeRef = useRef(currentScope);
    const prevWholesalerRef = useRef(currentWholesaler);
    const prevPartnerRef = useRef(currentPartner);
    const prevCompanyRef = useRef(currentCompany);

    useEffect(() => {
        const scopeChanged = prevScopeRef.current !== currentScope;
        const wholesalerChanged =
            prevWholesalerRef.current !== currentWholesaler;
        const partnerChanged = prevPartnerRef.current !== currentPartner;
        const companyChanged = prevCompanyRef.current !== currentCompany;

        if (
            scopeChanged ||
            wholesalerChanged ||
            partnerChanged ||
            companyChanged
        ) {
            if (mode !== WHOLESALER_MODES.MAIN_TABLE) {
                setMode(WHOLESALER_MODES.MAIN_TABLE);
                setSelectedWholesalerId(null);
            }
            prevScopeRef.current = currentScope;
            prevWholesalerRef.current = currentWholesaler;
            prevPartnerRef.current = currentPartner;
            prevCompanyRef.current = currentCompany;
        }
        // React Query will automatically refetch due to queryKey change
    }, [currentScope, currentWholesaler, currentPartner, currentCompany, mode]);

    const mainTable = (
        <Container
            as="div"
            maxW="container.xl"
            px={0}
            aria-label="wholesalers main table">
            <Text
                sx={{
                    fontSize: '20',
                    fontWeight: '500',
                    marginBottom: '15px',
                }}>
                Wholesalers
            </Text>
            <AGGridMain
                gridRef={gridRef}
                tableData={tableDataWithColumns}
                isLoading={isLoading}
                isFetching={isFetching}
                isRefetching={isRefetching}
                refetch={refetch}
                onColumnVisible={onColumnVisible}
                overlayNoRowsTemplate="No Wholesalers Found"
                titleName="Wholesalers"
                extraComponents={
                    currentScope >= 80 && (
                        <div style={{ paddingLeft: '20px' }}>
                            <Button
                                size="sm"
                                width="140px"
                                onClick={handleAdd}
                                data-walkthroughid={
                                    wholesalerWalkthroughIds?.general
                                        ?.ufAddButton
                                }>
                                + Add Wholesaler
                            </Button>
                        </div>
                    )
                }
            />
        </Container>
    );

    const modalContent = () => {
        switch (mode) {
            case WHOLESALER_MODES.MAIN_TABLE:
                return mainTable;
            case WHOLESALER_MODES.ADD:
                return (
                    <WholesalerEdit
                        mode="Add"
                        resetToMainTable={resetToMainTable}
                        switchToEditMode={(wholesalerId) => {
                            setSelectedWholesalerId(wholesalerId);
                            setMode(WHOLESALER_MODES.EDIT);
                        }}
                        selectedWholesalerId={null}
                    />
                );
            case WHOLESALER_MODES.EDIT:
                return (
                    <WholesalerEdit
                        mode="Edit"
                        resetToMainTable={resetToMainTable}
                        selectedWholesalerId={selectedWholesalerId}
                    />
                );
            case WHOLESALER_MODES.LOADING:
                return <LoadingIndicator />;
            default:
                return mainTable;
        }
    };

    return <>{modalContent()}</>;
};

export default Wholesalers;
